import {
  AspectRatio,
  Box,
  Button,
  Flex,
  Image,
  Link,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { POSTS_TYPE_VALUE_PAGE } from "@raiden/library/constants/posts";
import { useConfiguration } from "@raiden/library/hooks/useConfiguration";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { FormattedMessage } from "react-intl";
import { CenteredContent } from "../../../../components/CenteredContent";
import { Href } from "../../../../components/Href";
import { Menu } from "./Menu";

export function Footer() {
  const translate = useTranslate();

  const { configuration } = useConfiguration();

  const environment = configuration.environments?.[0];

  const postLegalNotice = configuration.posts?.[POSTS_TYPE_VALUE_PAGE]?.find(
    (post) => post.alias === process.env.NEXT_PUBLIC_POST_ALIAS_LEGAL_NOTICE,
  );

  return (
    <Box borderTop="1px solid" borderColor="brandSecondary.100" as="footer">
      <CenteredContent maxWidth="1920px">
        <Box
          backgroundImage={{
            base: "none",
            md: `url(${generateFrontPath({
              id: "@front.internal-assets",
              parameters: {
                filePath: "background-footer.svg",
              },
              includeBasePath: true,
            })})`,
          }}
          backgroundSize="auto 100%"
          backgroundPosition="right"
          backgroundRepeat="no-repeat"
          padding={{ base: "2.8rem .625rem", md: "2.8rem 2rem" }}>
          <CenteredContent>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap="2rem">
              <Stack spacing="1.5rem">
                <Href
                  href={generateFrontPath({
                    id: "@front.home",
                  })}>
                  <AspectRatio ratio={237 / 80} maxW="237px" maxH="80px">
                    <Image
                      src={generateFrontPath({
                        id: "@front.internal-assets",
                        parameters: { filePath: "logo.svg" },
                        includeBasePath: true,
                      })}
                      alt="Logo"
                      maxW="237px"
                      maxH="80px"
                    />
                  </AspectRatio>
                </Href>

                <Text
                  color="brandPrimary.900"
                  variant="text-large"
                  whiteSpace="pre-line">
                  {environment.infos}
                </Text>

                <Stack spacing=".15rem">
                  <Text variant="text-button" color="brandPrimary.900">
                    <Link
                      href={`tel:${environment.phone_number}`}
                      display="inline-block">
                      {environment.phone_number
                        .replace("+33", "0")
                        .replace(/(..)/g, "$1 ")}
                    </Link>
                  </Text>

                  <Text variant="text-button" color="brandPrimary.900">
                    <Link href={`mailto:${environment.email}`} target="_blank">
                      {environment.email}
                    </Link>
                  </Text>
                </Stack>

                <Box>
                  <Href href={generateFrontPath({ id: "@front.contact" })}>
                    <Button
                      colorScheme="brandPrimary"
                      h="52px"
                      fontSize="1.25rem"
                      fontWeight={400}>
                      <FormattedMessage defaultMessage="Nous contacter" />
                    </Button>
                  </Href>
                </Box>
              </Stack>

              <Stack spacing="1.5rem">
                <Text variant="h3">
                  <FormattedMessage defaultMessage="Liens utiles" />
                </Text>

                <Menu />
              </Stack>
            </SimpleGrid>

            <Flex flexWrap="wrap" gap="1rem" mt="24px" alignItems="center">
              <Text variant="text-small" color="brandSecondary.600">
                <FormattedMessage
                  defaultMessage="© {year} SARL Valentin Expertises. Tous droits réservés"
                  values={{
                    year: new Date().getFullYear(),
                  }}
                />
              </Text>

              <Text variant="text-small" color="brandSecondary.600">
                <FormattedMessage
                  defaultMessage="Conception et réalisation par <link>Splitfire</link>"
                  values={{
                    link: (chunks) => (
                      <Href href="https://www.splitfire.fr" target="_blank">
                        <Link as="span" textDecoration="underline">
                          {chunks}
                        </Link>
                      </Href>
                    ),
                  }}
                />
              </Text>

              {postLegalNotice && (
                <Href
                  href={generateFrontPath({
                    id: "@front.page",
                    parameters: {
                      postSlug: `${postLegalNotice?.id}-${translate(
                        postLegalNotice?.slug,
                      )}`,
                    },
                  })}>
                  <Link
                    as="span"
                    textDecoration="underline"
                    fontSize=".875rem"
                    color="brandSecondary.600">
                    <FormattedMessage defaultMessage="Mentions légales" />
                  </Link>
                </Href>
              )}
            </Flex>
          </CenteredContent>
        </Box>
      </CenteredContent>
    </Box>
  );
}
